/**
 * InfraVerdictBoard — V105-4-6, the third-party verdict board in a FedRAMP / GovRAMP program's
 * Scope tab (`?view=verdicts`), next to the inventory review (ADR-0072 §3, D-105-9).
 *
 *   - One row per service the product depends on (added or linked in the inventory): its verdict
 *     at the program's impact level, why in one line (outcome first), the other level's verdict
 *     when it differs. A row blocked on GAC Pro content shows a neutral "Waiting" chip only.
 *   - One count split from the server (`buckets`, `attention_count`): the summary, the "Needs you"
 *     tab and the Scope-tab callout always agree.
 *   - Every row that needs something carries ONE next step (the top row's is the primary). A step
 *     only an owner / admin can take shows "Ask an admin" with the reason; a disclosure a lead
 *     can't edit (provider outside this program's boundary) offers adding it to the boundary.
 *   - Forms open after their button, take focus, close with Cancel / Esc and hand focus back to
 *     the control that opened them; errors show inside the form, the first invalid field focused.
 *   - Done state as soon as nothing needs you: save the verdicts, then download the disclosure, then
 *     compare strategies (V105-5-5, `onCompare`).
 *
 * Toasts go through the Scope tab's single host (`toastApi`, A-H1); standalone it keeps its own.
 * Reads (any member). Writes follow the server: `can_write`, per item `can_edit_tpr` and
 * `can_override_q3`; 403s read as plain text. Readers get one "Ask an admin" path.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, Radio, Select, Spinner, Textarea, Toast } from '@aigovops/design-system';
import type { BoardAnswer, InfraVerdict } from '@aigovops/core';
import { ApiError, type CoverageDocumentKind } from '../../lib/api';
import {
  attestEvidence, getTprExport, getVerdicts, markPartition, overrideCoverage, recomputeVerdicts, setInventoryFunction, snapshotVerdicts, updateServiceTpr,
  withdrawCoverage, withdrawEvidence, type StrategyDecisionRef, type VerdictBoard, type VerdictBoardItem,
} from '../../lib/infra-plan-api';
import { programLabel } from '../../lib/boundary-model';
import { fmtDate } from '../../lib/format';
import { ClassificationForm, FUNCTION_LABEL } from './InfraInventoryReview';
import type { ToastApi, ProgramToast } from './program-toast';
import { AskAdmin } from '../AskAdmin';
import './infra-inventory.css';
import './verdict-board.css';
import '../sweep/sweep.css';

type Item = VerdictBoardItem;

// ---- plain-language model (exported for tests) ------------------------------------------------------------

export const VERDICT_LABEL: Record<InfraVerdict, string> = {
  replace: 'Replace', partition: 'Partition', documented_third_party: 'Documented third party', out_of_scope: 'Out of scope', pending: 'Needs answers',
};
const VERDICT_TONE: Record<InfraVerdict, 'bad' | 'warn' | 'info' | 'ok' | undefined> = {
  replace: 'bad', partition: 'info', documented_third_party: 'ok', out_of_scope: undefined, pending: 'warn',
};
const ORDER: InfraVerdict[] = ['pending', 'replace', 'partition', 'documented_third_party', 'out_of_scope'];
const TARGET_NAME: Record<string, string> = { aws: 'AWS GovCloud', azure: 'Azure Government', gcp: 'Google Cloud (Assured Workloads)', fedstart: 'Palantir FedStart', knox: 'Knox', game_warden: 'Game Warden' };
const STRATEGY_NAME: Record<string, string> = { hybrid: 'Hybrid', single_hyperscaler: 'One government cloud', inherit_boundary: 'Inherit a boundary', stay_commercial: 'Stay commercial' };
export const LEVEL_LABEL: Record<string, string> = { low: 'Low', moderate: 'Moderate', high: 'High' };
/** Every engine `missing` key (infra-verdict.ts missingInputs). */
export const PENDING_TEXT: Record<string, string> = {
  q1: 'Say whether it holds federal data', q2: 'Say whether it could affect federal data', function: 'Say what it does',
  q3: 'Provider coverage not checked yet', 'q5.precedent_count': 'Waiting on published FedRAMP packages', 'q3.coverage_conflict': 'Sources disagree on provider coverage',
};
/** Owner decision 2026-10-09: one action settles a coverage question either way (covered / not covered). */
export const COVERAGE_ACTION = 'Say whether it’s covered';
/** The four disclosure fields. */
export const TPR_LABEL: Record<string, string> = {
  tpr_usage: 'How the product uses it', tpr_justification: 'Why it is needed', tpr_mitigations: 'How the risk is reduced', compensating_controls: 'Compensating controls',
};
const TPR_SHORT: Record<string, string> = { tpr_usage: 'use', tpr_justification: 'justification', tpr_mitigations: 'mitigations', compensating_controls: 'compensating controls' };
/** Catalog partition kinds (ADR-0075 §6 enum), in plain words. */
export const PATTERN_LABEL: Record<string, string> = {
  federal_tenants_on_authorized: 'Federal workspaces use an authorized service; this one never receives their data',
  reference_class_only: 'It holds public reference content only',
  metadata_only: 'It carries links and notifications only',
  customer_owned_provider: 'Federal customers bring their own provider',
};
/** The buckets of the one count split, in board order. */
export const BUCKETS: ReadonlyArray<[keyof VerdictBoard['buckets'], string]> = [
  ['needs_answers', 'need answers'], ['replace', 'replace'], ['partition', 'partition'], ['documented_third_party', 'documented'], ['out_of_scope', 'out of scope'], ['waiting', 'waiting on GAC Pro'],
];
export const label = (map: Record<string, string>, key: string | null | undefined, fallback: string): string => (key && map[key]) || fallback;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const patternKind = (id: string | null | undefined) => (id ? id.slice(id.lastIndexOf(':') + 1) : null);

/** One plain line for why: the outcome first, then the reason. */
export function verdictWhy(item: Pick<Item, 'verdict'>): string {
  const v = item.verdict;
  if (v.verdict === 'pending') return v.missing.map((m) => label(PENDING_TEXT, m, 'An answer is missing')).join(' · ');
  if (v.verdict === 'out_of_scope') return 'Out of scope: no federal data, and no effect on it';
  if (v.verdict === 'partition') return v.residual?.verdict === 'out_of_scope' ? 'Keep federal data off it; then it is out of scope' : 'Keep federal data off it; then document it';
  if (v.verdict === 'replace') {
    if (v.labels.includes('catalog_awaiting_review')) return 'Replace for now: provider coverage is still in review';
    if (v.rule === 'R3b') return 'Replace: on the core data path, and no other package uses a service like it';
    // R3a from a person's "not covered" answer (an attested "covered" never reads as unmet).
    return v.labels.includes('q3_override') ? 'Replace: you said the provider doesn’t cover it' : 'Replace: it holds federal data and no authorized offering covers it';
  }
  // V109-5-3 (D3): a third party is never called "certified" here. A listing is the provider's OFFERING on a
  // public list (and only when that offering covers this product); anything else says what is and isn't listed.
  const parts = [v.subtype === 'certified' ? 'Documented; the provider’s offering that covers it is listed' : 'Documented; no listed offering covers it (allowed)'];
  if (v.reasons.includes('DOC_PRODUCT_NOT_LISTED')) parts[0] = 'Documented; offering authorized; product not listed';
  if (v.reasons.includes('DOC_OFFERING_MOVE')) parts.push('covered after moving to the provider’s authorized offering');
  if (v.reasons.includes('DOC_NO_PRECEDENT')) parts.push('no other package lists a service like it');
  return parts.join('; ');
}

/**
 * V109-5-3: what the FedRAMP Marketplace lists for this service, in words, never "certified": the listed
 * offering at the program's level, below it, "offering authorized; product not listed", or not listed.
 */
export function listingText(item: Pick<Item, 'q3_offerings' | 'verdict'>, level: string): string {
  if (item.verdict.reasons.includes('DOC_PRODUCT_NOT_LISTED')) return 'Offering authorized; product not listed';
  const covered = item.q3_offerings.filter((o) => o.state === 'covered');
  const at = covered.find((o) => o.at_level);
  if (at) return `Listed · ${label(LEVEL_LABEL, at.impact_level, at.impact_level)}`;
  if (covered.length) return `Listed below ${level}`;
  return 'Not listed';
}

/** Built-UI #8: what the GovRAMP product list says about the service (a person's answer says so), never "certified". */
export function govrampListingText(item: Pick<Item, 'govramp_listing'>): string {
  const g = item.govramp_listing;
  if (!g || g.source === 'unknown' || g.status === 'unknown') return 'Not listed';
  const status = g.status.replace(/_/g, ' ');
  return `${g.source === 'attested' ? 'You said: ' : 'Listed · '}${status.charAt(0).toUpperCase()}${status.slice(1)}${g.impact ? ` · ${label(LEVEL_LABEL, g.impact, g.impact)}` : ''}`;
}

/** V109-5-3: a source older than this reads amber with its age (freshness per source). */
export const SOURCE_STALE_DAYS = 7;
export function sourceFreshness(readAt: string | null | undefined, now = Date.now()): { text: string; stale: boolean } {
  if (!readAt) return { text: 'not read yet', stale: true };
  const days = Math.floor((now - new Date(readAt).getTime()) / 86_400_000);
  const when = days <= 0 ? 'read today' : days === 1 ? 'read yesterday' : `read ${fmtDate(readAt)}`;
  return days > SOURCE_STALE_DAYS ? { text: `${when} · ${days} days old`, stale: true } : { text: when, stale: false };
}

/** Built-UI #8: how many verdicts on the page read a source (its warning is amber only when one does). */
export function sourceDependents(key: string, items: readonly Pick<Item, 'q3_offerings' | 'verdict' | 'govramp_listing'>[], program: string): number {
  if (key === 'fedramp_marketplace') return items.filter((i) => i.q3_offerings.length > 0).length;
  if (key === 'govramp_product_list') return program === 'govramp' ? items.filter((i) => i.govramp_listing && i.govramp_listing.source === 'govramp_list').length : 0;
  return items.filter((i) => i.verdict.reasons.some((r) => r === 'DOC_PRODUCT_NOT_LISTED' || r === 'REPLACE_INHERITANCE_UNMET')).length;
}

/**
 * Built-UI #8: a source's line — never a warning without what happens next. Fresh: "read today". Stale:
 * its age and when GAC Pro reads it again ("re-reads it by Oct 15", from the source's cadence), amber
 * only when a verdict on the page depends on it.
 */
export function sourceLine(s: { read_at: string | null; next_read_by?: string | null }, dependents: number, now = Date.now()): { text: string; tone: 'ok' | 'quiet' | 'warn' } {
  const f = sourceFreshness(s.read_at, now);
  if (!f.stale) return { text: f.text, tone: 'ok' };
  const next = s.next_read_by ? `GAC Pro re-reads it by ${fmtDate(s.next_read_by)}` : 'GAC Pro re-reads it when a new list is published';
  return { text: `${f.text} · ${next}`, tone: dependents > 0 ? 'warn' : 'quiet' };
}

const SOURCE: Record<string, string> = { attested: 'A person answered', connector: 'From your connections and code', marketplace: 'From the FedRAMP Marketplace', corpus: 'From published FedRAMP packages' };
interface AnswerLine { key: string; label: string; value: string; source: string | null; note?: string }

/** The five answers in plain words (no rule ids, no identifiers). */
export function answerLines(item: Item): AnswerLine[] {
  const a = item.answers;
  const src = (x: BoardAnswer | undefined) => (x ? label(SOURCE, x.provenance, 'Recorded') : null);
  const q1 = a.q1?.value as string | undefined;
  const q3 = a.q3?.value as { required: boolean; status: string; coverage: { covered: boolean; current_offering_covered: boolean; state?: string | null }; required_objective_ids?: string[]; catalog_state?: string; override?: { offering: string | null; covered?: boolean } } | undefined;
  const q4 = a.q4?.value as { status: string; attested_method?: string } | undefined;
  const q5 = a.q5?.value as { tpr_missing: string[]; precedent_count: number | null } | undefined;
  const lines: AnswerLine[] = [
    { key: 'q1', label: 'Holds federal data', value: q1 === 'yes' ? 'Yes' : q1 === 'metadata_only' ? 'Only account or service metadata' : q1 === 'no' ? 'No' : 'Not answered', source: src(a.q1) },
  ];
  if (q1 === 'no') lines.push({ key: 'q2', label: 'Could affect federal data', value: a.q2 ? (a.q2.value === 'yes' ? 'Yes' : 'No') : 'Not answered', source: src(a.q2) });
  let inherit = 'Not checked yet';
  if (q3) {
    if (q3.override?.covered === false) inherit = 'Not covered (your answer)';
    else if (q3.override) inherit = `Covered by ${q3.override.offering} (your answer)`;
    else if (!q3.required) inherit = 'Not needed';
    else if (q3.coverage.state === 'conflict') inherit = 'Sources disagree; the provider’s confirmation or your answer settles it';
    else if (q3.catalog_state === 'awaiting_review') inherit = 'Provider coverage is still in review';
    else if (q3.status === 'satisfiable') inherit = q3.coverage.current_offering_covered ? 'Covered by the provider’s authorized offering' : 'Covered after a move to the provider’s authorized offering';
    else inherit = 'No authorized offering covers it';
  }
  const n = q3?.required ? q3.required_objective_ids?.length ?? 0 : 0;
  lines.push({ key: 'q3', label: 'Controls from the provider', value: inherit, source: src(a.q3), note: n ? `${plural(n, 'control objective')} depend on it` : undefined });
  lines.push({
    key: 'q4', label: 'Automated evidence',
    value: !q4 ? 'Not checked yet' : q4.attested_method ? `You collect it: ${q4.attested_method}` : q4.status === 'automated' ? 'Collected automatically' : q4.status === 'partial' ? 'Connected, but no test reads it yet' : 'No connector collects it',
    source: src(a.q4),
  });
  const missing = q5?.tpr_missing ?? [];
  const prec = q5?.precedent_count;
  lines.push({
    key: 'q5', label: 'Disclosure', value: !q5 ? 'Not checked yet' : missing.length ? `Missing ${missing.map((f) => label(TPR_SHORT, f, 'a field')).join(', ')}` : 'Complete', source: src(a.q5),
    note: typeof prec === 'number' ? `${plural(prec, 'published package')} list a service like it` : undefined,
  });
  return lines;
}

type Panel = 'answer' | 'function' | 'coverage' | 'evidence' | 'tpr' | 'partition';
export type RowAction =
  | { kind: 'none' } | { kind: 'waiting'; text: string } | { kind: 'reader' }
  | { kind: 'ask_admin'; verb: string; subject: string; line: string }
  | { kind: 'boundary'; href: string; label: string; verb: string; subject: string }
  | { kind: 'panel'; panel: Panel; label: string } | { kind: 'link'; href: string; label: string } | { kind: 'check_coverage'; label: string };
const HOSTING = new Set(['primary_data_store', 'secondary_data_store', 'compute']);
/** Actions the caller can take (excludes waiting, readers and admin-only rows). */
export const actionable = (a: RowAction) => a.kind === 'panel' || a.kind === 'link' || a.kind === 'check_coverage' || a.kind === 'boundary';

/** The row's single next step, derived from what the app knows (pure; tested). */
export function rowAction(item: Item, canWrite: boolean, ctx: { prefix: string; program: string; uncheckable?: ReadonlySet<string> }): RowAction {
  const v = item.verdict;
  if (item.attention === 'waiting') return { kind: 'waiting', text: v.verdict === 'replace' ? 'Waiting on coverage review' : 'Waiting on GAC Pro content' };
  if (!canWrite) return item.attention === 'needs_you' ? { kind: 'reader' } : { kind: 'none' };
  if (v.missing.includes('function')) return { kind: 'panel', panel: 'function', label: 'Set what it does' };
  if (v.missing.includes('q1') || v.missing.includes('q2') || item.suggestions > 0) return { kind: 'panel', panel: 'answer', label: 'Answer' };
  if (v.missing.includes('q3')) {
    return ctx.uncheckable?.has(item.item_id) ? { kind: 'waiting', text: `Coverage can’t be checked for ${item.provider_name} yet` } : { kind: 'check_coverage', label: 'Check coverage now' };
  }
  if (v.missing.includes('q3.coverage_conflict')) {
    return item.can_override_q3 ? { kind: 'panel', panel: 'coverage', label: COVERAGE_ACTION }
      : { kind: 'ask_admin', verb: 'say whether the provider covers it', subject: `${item.name}: provider coverage`, line: 'Only an owner or admin records provider coverage' };
  }
  if (v.verdict === 'replace' && item.replacement_planned) {
    const href = item.component_ids.length === 1 ? `${ctx.prefix}/programs/${ctx.program}/scope/replace/${item.component_ids[0]}` : `${ctx.prefix}/programs/${ctx.program}/scope/move`;
    return { kind: 'link', href, label: 'See the plan' };
  }
  if (v.verdict === 'replace') {
    if (item.component_ids.length === 1) return { kind: 'link', href: `${ctx.prefix}/programs/${ctx.program}/scope/replace/${item.component_ids[0]}`, label: 'Plan the replacement' };
    return { kind: 'link', href: `${ctx.prefix}/programs/${ctx.program}/scope/move`, label: HOSTING.has(item.function ?? '') ? 'Plan the move' : 'Plan the replacement' };
  }
  if (v.verdict === 'partition' && !item.partition_confirmation) return { kind: 'panel', panel: 'partition', label: 'Plan the partition' };
  if (v.findings.some((f) => f.kind === 'infra_tpr_gap')) {
    if (item.can_edit_tpr) return { kind: 'panel', panel: 'tpr', label: 'Fill in the disclosure' };
    // A lead edits a disclosure only for a provider a system in this program's boundary uses.
    // Lands on the components that use this service, with the first one's switch focused (or Add component prefilled).
    return { kind: 'boundary', href: `${ctx.prefix}/programs/${ctx.program}/scope?view=components&uses=${encodeURIComponent(item.external_service_id ?? '')}`, label: `Add to the ${programLabel(ctx.program)} boundary`, verb: 'fill in the disclosure', subject: `${item.name}: disclosure` };
  }
  if (v.findings.some((f) => f.kind === 'infra_evidence_gap')) {
    const kinds = ((item.answers.q4?.value as { connector_kinds?: string[] } | undefined)?.connector_kinds) ?? [];
    return kinds.length ? { kind: 'link', href: `${ctx.prefix}/setup?connector=${encodeURIComponent(kinds[0]!)}`, label: 'Connect a collector' } : { kind: 'panel', panel: 'evidence', label: 'Record how you collect evidence' };
  }
  return { kind: 'none' };
}

const sortRows = (rows: readonly Item[]) => rows.slice()
  .sort((x, y) => ORDER.indexOf(x.verdict.verdict) - ORDER.indexOf(y.verdict.verdict) || (x.name < y.name ? -1 : x.name > y.name ? 1 : 0));

/** The header's one next step: the top row the caller can act on, plus how many need an admin. Readers: null. */
export function nextStepLine(items: readonly Item[], canWrite: boolean, ctx: { prefix: string; program: string; uncheckable?: ReadonlySet<string> }): string | null {
  if (!canWrite) return null;
  const need = sortRows(items.filter((i) => i.attention === 'needs_you'));
  const can = need.filter((i) => actionable(rowAction(i, true, ctx)));
  const admin = need.filter((i) => rowAction(i, true, ctx).kind === 'ask_admin').length;
  const tail = admin ? ` · ${plural(admin, 'service')} ${admin === 1 ? 'needs' : 'need'} an admin` : '';
  const top = can[0];
  if (!top) return admin ? `${plural(admin, 'service')} ${admin === 1 ? 'needs' : 'need'} an admin` : null;
  const a = rowAction(top, true, ctx) as { label: string };
  return `Next: ${a.label} · ${top.name}${can.length > 1 ? ` (+${can.length - 1} more)` : ''}${tail}`;
}

const errMsg = (e: unknown, fallback: string) => {
  if (e instanceof ApiError && e.status === 403) return 'You can’t change this one. An owner, an admin or the program lead whose boundary uses this service can.';
  return e instanceof Error && e.message ? e.message : fallback;
};

/** Focusable controls outside any closed <details> (A-H2). */
const visibleControls = (root: Element | null | undefined, sel: string): HTMLElement[] =>
  [...(root?.querySelectorAll<HTMLElement>(sel) ?? [])].filter((el) => !el.closest('details:not([open])') || el.tagName === 'SUMMARY');

interface ToastState extends ProgramToast { version: number }
interface TprIssue { name: string; text: string; serviceId: string | null }

// ---- the board -------------------------------------------------------------------------------------------

export function InfraVerdictBoard({ slug, prefix, program, onBack, onOpenInventory, onCompare, openPanel, toastApi }: {
  slug: string; prefix: string; program: string; onBack: () => void; onOpenInventory: () => void;
  /** V105-5-5: open the strategy comparison (the step after the verdicts). */
  onCompare?: () => void;
  /** V105-5-5 R2-H4: open one row's panel on arrival (the strategy view's "Record the coverage"). */
  openPanel?: { itemId: string; panel: 'coverage' } | null;
  /** V105-4-6 A-H1: the Scope tab's single toast host (else the board renders its own). */
  toastApi?: ToastApi;
}) {
  const qc = useQueryClient();
  const key = useMemo(() => ['infra-plan', slug, 'verdicts', program] as const, [slug, program]);
  const board = useQuery({ queryKey: key, queryFn: () => getVerdicts(slug, program) });
  const [tab, setTab] = useState<'attention' | 'all'>('attention');
  const [sticky, setSticky] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<{ itemId: string; panel: Panel; opener: HTMLElement | null } | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [error, setError] = useState<{ text: string; retry?: () => void } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [issues, setIssues] = useState<TprIssue[] | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const [downloadedAt, setDownloadedAt] = useState<string | null>(null);
  const [uncheckable, setUncheckable] = useState<Set<string>>(new Set());
  const heading = useRef<HTMLHeadingElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const focusAfter = useRef<{ itemId: string; opener?: HTMLElement | null; confirm?: boolean; q2?: boolean } | null>(null);
  const version = useRef(0);
  const recorded = useRef(false);
  const show = (t: ProgramToast) => {
    if (toastApi) { toastApi.show({ ...t, returnFocus: t.returnFocus ?? heading.current }); return; }
    version.current += 1; setToast({ ...t, version: version.current });
  };

  const setBoard = (b: VerdictBoard) => qc.setQueryData(key, (cur: { board: VerdictBoard; can_write: boolean; strategy_decision?: StrategyDecisionRef | null } | undefined) => ({ board: b, can_write: cur?.can_write ?? false, strategy_decision: cur?.strategy_decision ?? null }));
  /** Recompute and show the new state; a failure shows Retry instead of a success. */
  const recompute = async (): Promise<VerdictBoard | null> => {
    try { const r = await recomputeVerdicts(slug, program); setBoard(r.board); await qc.invalidateQueries({ queryKey: ['infra-plan', slug, 'items'] }); return r.board; }
    catch (e) { setError({ text: errMsg(e, 'The verdicts could not be updated.'), retry: () => { setError(null); void recompute(); } }); return null; }
  };
  const data = board.data;
  // H10: the view heading takes focus once the board has rendered.
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (focusedOnce.current || !data || !heading.current) return;
    focusedOnce.current = true;
    // Round-3 H5: a deep link to a row's panel owns the focus (the panel's first field, or the row heading).
    if (openPanel && data.board.items.some((i) => i.item_id === openPanel.itemId)) return;
    heading.current.focus();
  });
  // R2-H4: a deep link opens that row's coverage panel once (owner / admin rows only; else the row is shown).
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || !data || !openPanel) return;
    const it = data.board.items.find((i) => i.item_id === openPanel.itemId);
    if (!it) return;
    deepLinked.current = true;
    setTab('all');
    if (it.can_override_q3) setOpen({ itemId: it.item_id, panel: 'coverage', opener: null });
    else window.requestAnimationFrame(() => document.getElementById(`vb-name-${it.item_id}`)?.focus());
  }, [data, openPanel]);
  // Writers: record the answers computed from current content once (idempotent; supersedes only on change).
  useEffect(() => {
    if (!data?.can_write || recorded.current || !data.board.stale) return;
    recorded.current = true;
    void recompute();
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // H8 / A-H2 / A-H3: after a change or a close, focus the opener, else the row's next action, else the
  // next row's, else the row's heading; never a control inside a closed <details>.
  useEffect(() => {
    const f = focusAfter.current;
    if (!f || !list.current) return;
    focusAfter.current = null;
    const raf = window.requestAnimationFrame(() => {
      const rows = [...(list.current?.querySelectorAll<HTMLLIElement>('li[data-row]') ?? [])];
      const mine = rows.find((r) => r.dataset.row === f.itemId);
      if (f.confirm && mine) {
        const c = visibleControls(mine, 'button[aria-label^="Confirm:"]')[0];
        if (c) { c.focus(); return; }
      }
      if (f.q2 && mine) {
        const q = mine.querySelector<HTMLElement>('.gp-vb-panel select[data-q="q2"]');
        if (q) { q.focus(); return; }
      }
      if (f.opener?.isConnected && !f.opener.closest('details:not([open])')) { f.opener.focus(); return; }
      const next = rows[rows.indexOf(mine!) + 1] ?? rows[0];
      const target = (mine && (visibleControls(mine, '[data-row-action]')[0] ?? mine.querySelector<HTMLElement>('h4')))
        ?? (next && (visibleControls(next, '[data-row-action]')[0] ?? next.querySelector<HTMLElement>('h4')))
        ?? heading.current;
      target?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  });

  if (board.isLoading) return <Spinner label="Loading the verdicts" />;
  if (board.error || !data) return <Alert variant="error"><p>The verdicts could not load.</p><Button compact onClick={() => void board.refetch()}>Retry</Button></Alert>;
  const b = data.board;
  const readAt: Record<string, string | null> = Object.fromEntries((b.sources ?? []).map((s) => [s.key, s.read_at]));
  const canWrite = data.can_write;
  const ctx = { prefix, program, uncheckable };
  const level = label(LEVEL_LABEL, b.target_impact_level, 'the program’s level');
  // Save waits only on answers this caller can give; rows that need an admin are named instead.
  const blocking = b.items.filter((i) => i.verdict.verdict === 'pending' && i.attention === 'needs_you' && actionable(rowAction(i, canWrite, ctx))).length;
  const adminOnly = canWrite ? b.items.filter((i) => i.attention === 'needs_you' && rowAction(i, true, ctx).kind === 'ask_admin').length : 0;
  const attentionRows = b.items.filter((i) => i.attention === 'needs_you' || sticky.has(i.item_id));
  const rows = sortRows(tab === 'attention' ? attentionRows : b.items);
  const topId = rows.find((r) => r.attention === 'needs_you' && actionable(rowAction(r, canWrite, ctx)))?.item_id ?? null;
  const next = nextStepLine(b.items, canWrite, ctx);
  const done = b.items.length > 0 && b.attention_count === 0;
  const downloadFirst = !!b.last_snapshot && (!downloadedAt || downloadedAt < b.last_snapshot.created_at);
  // V105-5-5 H6 (server state): once the verdicts are saved, comparing strategies is the next step until a strategy is
  // chosen for that save; once one is, the board names it with a View link.
  const chosen = data.strategy_decision ?? null;
  const compareFirst = !!onCompare && !!b.last_snapshot && (!chosen || chosen.snapshot_id !== b.last_snapshot.snapshot_id);
  const compareBtn = (variant: 'primary' | 'ghost') => (onCompare ? <Button compact variant={variant} onClick={onCompare}>Compare strategies</Button> : null);
  const chosenLine = chosen && onCompare ? (
    <p className="gp-dim gp-vb-strategy">{`Strategy: ${STRATEGY_NAME[chosen.family] ?? 'chosen'}${chosen.anchor ? ` · ${TARGET_NAME[chosen.anchor] ?? chosen.anchor}` : ''}, chosen ${fmtDate(chosen.decided_at)} · `}<button type="button" className="gp-cb-textbtn" onClick={onCompare}>View<span className="sr-only"> the chosen strategy</span></button></p>
  ) : null;
  const switchTab = (t: 'attention' | 'all') => { setTab(t); setSticky(new Set()); };
  const ask = (verb: string, subject: string) => <AskAdmin slug={slug} compact verb={verb} what="runs the FedRAMP or GovRAMP program" subject={subject} />;

  const toastFor = (nb: VerdictBoard | null, item: Item, msg: string) => {
    const now = nb?.items.find((i) => i.item_id === item.item_id);
    show({ message: `${msg} · ${item.name}${now ? ` · now ${VERDICT_LABEL[now.verdict.verdict]}` : ''}` });
  };
  /** After a row's write: recompute, toast "<msg> · now <verdict>", keep the row in view, move focus. */
  const changed = async (item: Item, msg: string, opts: { keepOpen?: boolean; confirm?: boolean; q2?: boolean } = {}) => {
    setSticky((s) => new Set(s).add(item.item_id));
    focusAfter.current = { itemId: item.item_id, confirm: opts.confirm, q2: opts.q2 };
    if (!opts.keepOpen) setOpen(null);
    const nb = await recompute();
    // "Suggestion ready" is already announced by the form's own live region: no second announcement.
    if (nb && !opts.confirm) toastFor(nb, item, msg);
  };
  /** A write that returns the board; errors go back to the open form (rethrown). */
  const boardWrite = async (item: Item, fn: () => Promise<{ board: VerdictBoard }>, msg: string) => {
    const r = await fn();
    setBoard(r.board); setSticky((s) => new Set(s).add(item.item_id));
    focusAfter.current = { itemId: item.item_id };
    setOpen(null);
    toastFor(r.board, item, msg);
  };
  const check = async (item: Item, opener: HTMLElement | null) => {
    if (busy) return;
    setBusy(`check:${item.item_id}`);
    try {
      const nb = await recompute();
      const now = nb?.items.find((i) => i.item_id === item.item_id);
      if (now?.verdict.missing.includes('q3')) setUncheckable((s) => new Set(s).add(item.item_id)); // no loop
      focusAfter.current = { itemId: item.item_id, opener };
      if (nb) toastFor(nb, item, 'Coverage checked');
    } finally { setBusy(null); }
  };

  const save = async () => {
    if (busy || blocking) return;
    setError(null); setBusy('snapshot');
    try { const r = await snapshotVerdicts(slug, program); setSaved(r.snapshot.item_count); show({ message: `Saved ${plural(r.snapshot.item_count, 'verdict')}`, details: `At ${level}` }); await board.refetch(); }
    catch (e) { setError({ text: errMsg(e, 'The verdicts could not be saved.') }); } finally { setBusy(null); }
  };
  const download = async () => {
    if (busy) return;
    setError(null); setBusy('download');
    try {
      const r = await getTprExport(slug, program);
      const blob = new Blob([JSON.stringify(r.export, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `third-party-disclosure-${program}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setDownloadedAt(new Date().toISOString());
      const found: TprIssue[] = [];
      for (const e of r.export.entries ?? []) {
        if (e.missing.length) found.push({ name: e.name, serviceId: e.external_service_id, text: `missing ${e.missing.map((f) => label(TPR_SHORT, f, 'a field')).join(', ')}` });
      }
      const nonCert = r.export.thirdPartyInformationResources?.nonCertified ?? [];
      for (const v of r.validation_errors) {
        const m = /\/nonCertified\/(\d+)\/?(\w*)/.exec(v);
        const name = m ? nonCert[Number(m[1])]?.name ?? 'A service' : 'The disclosure';
        found.push({ name, serviceId: m ? (r.export.entries ?? []).find((x) => x.name === name)?.external_service_id ?? null : null, text: m?.[2] === 'website' ? 'its website is not a valid address' : 'a field does not match the FedRAMP format' });
      }
      setIssues(found);
      show({ message: 'Disclosure downloaded', details: found.length ? `${plural(found.length, 'issue')} to fix` : 'Matches the FedRAMP format' });
    } catch (e) { setError({ text: errMsg(e, 'The disclosure could not be built.') }); } finally { setBusy(null); }
  };
  const openFor = (serviceId: string | null) => {
    const it = b.items.find((i) => i.external_service_id && i.external_service_id === serviceId);
    if (!it) return;
    setTab('all'); setOpen({ itemId: it.item_id, panel: 'tpr', opener: document.activeElement as HTMLElement | null });
  };
  const closePanel = () => {
    if (open) focusAfter.current = { itemId: open.itemId, opener: open.opener };
    setOpen(null);
  };
  const last = b.last_snapshot;
  const saveBtn = (variant: 'primary' | 'ghost') => (
    <Button compact variant={variant} aria-busy={busy === 'snapshot' || undefined} aria-disabled={busy === 'snapshot' || !!blocking || undefined}
      aria-describedby={blocking ? 'vb-save-why' : undefined} onClick={() => void save()}>{busy === 'snapshot' ? 'Saving…' : 'Save these verdicts'}</Button>
  );
  const downloadBtn = (variant: 'primary' | 'ghost') => (
    <Button compact variant={variant} title="JSON for the FedRAMP package" aria-busy={busy === 'download' || undefined} aria-disabled={busy === 'download' || undefined} onClick={() => void download()}>
      {busy === 'download' ? 'Building…' : 'Download disclosure'}
    </Button>
  );

  return (
    <Card>
      <div className="gp-inv gp-vb">
        <button type="button" className="gp-cb-back" onClick={onBack}>‹ Back to components</button>
        <div className="gp-cb-head">
          <h3 className="gp-card-h" id="vb-h" ref={heading} tabIndex={-1}>
            {`Third-party verdicts · ${level}${b.target_level_source === 'default' ? ' (assumed)' : ''}`}
          </h3>
          <span className="gp-actions">
            {b.target_level_source === 'default' ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" to={`${prefix}/programs/${program}`}>Set the level</Link> : null}
            {b.items.length && !done ? downloadBtn(downloadFirst && saved === null && !topId ? 'primary' : 'ghost') : null}
          </span>
        </div>
        {/* V109-5-3 / built-UI #8: each source the verdicts read, when GAC Pro last read it and, when it is old, when it
            reads it next; amber only when a verdict on this page depends on it. The current program's list first. */}
        {b.sources?.length ? (
          <ul className="sw-sources" aria-label="Sources" data-part="verdict-sources">
            <li><b>Sources</b></li>
            {[...b.sources].sort((x, y) => Number(y.key === 'govramp_product_list' && program === 'govramp') - Number(x.key === 'govramp_product_list' && program === 'govramp')).map((s) => {
              const l = sourceLine(s, sourceDependents(s.key, b.items, program));
              return <li key={s.key} data-source={s.key}><span>{s.label}</span><span className="sw-src-when" data-tone={l.tone}>{l.tone === 'warn' ? <span aria-hidden="true">⚠ </span> : null}{l.text}</span></li>;
            })}
          </ul>
        ) : null}
        {next ? <p className="gp-vb-next"><b>{next}</b></p> : null}
        {chosenLine}
        {error ? (
          <Alert variant="error" onDismiss={() => setError(null)}>
            <p>{error.text}</p>{error.retry ? <Button compact onClick={error.retry}>Retry</Button> : null}
          </Alert>
        ) : null}
        {b.catalog_state === 'awaiting_review' && (b.counts.replace || b.buckets.waiting) ? (
          <Alert variant="info"><p>Provider coverage is still in review; Replace verdicts may change once it is approved.</p></Alert>
        ) : null}
        {saved !== null ? (
          // Not a live region: the toast already announces the save.
          <div className="gp-cb-callout" data-tone="info">
            <span><b>{`Saved ${plural(saved, 'verdict')}`}</b> <span className="gp-dim">next, the disclosure for the package</span></span>
            <span className="gp-actions">{compareFirst ? compareBtn('primary') : null}{downloadBtn(compareFirst ? 'ghost' : 'primary')}<button type="button" className="gp-cb-textbtn" onClick={() => setSaved(null)}>Dismiss</button></span>
          </div>
        ) : null}
        {issues && issues.length ? (
          <Alert variant="warning" onDismiss={() => setIssues(null)}>
            <p><b>{`The disclosure has ${plural(issues.length, 'issue')}`}</b></p>
            <ul className="gp-vb-issues">
              {issues.map((x, i) => (
                <li key={`${x.name}-${i}`}>
                  <span>{`${x.name}: ${x.text}`}</span>
                  {x.serviceId && b.items.some((it) => it.external_service_id === x.serviceId && it.can_edit_tpr) ? <Button compact onClick={() => openFor(x.serviceId)} aria-label={`Fill in · ${x.name}`}>Fill in</Button> : null}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {b.items.length ? (
          <p className="gp-cb-sum">
            {BUCKETS.filter(([k]) => b.buckets[k]).map(([k, l], i) => (
              <span key={k}>{i ? <span aria-hidden="true">· </span> : null}<b>{b.buckets[k]}</b>{` ${l}`}</span>
            ))}
          </p>
        ) : null}
        {!canWrite && b.attention_count ? <div className="gp-vb-ask"><span>{`${plural(b.attention_count, 'service')} ${b.attention_count === 1 ? 'needs' : 'need'} an admin`}</span>{ask('answer the verdict questions', 'GAC Pro verdicts: services need answers')}</div> : null}

        {b.open_items ? (
          <div className="gp-cb-callout" data-tone="info">
            <span><b>{`${plural(b.open_items, 'service')} still to review`}</b> <span className="gp-dim">they get a verdict once added</span></span>
            <Button compact onClick={onOpenInventory}>{`Review ${b.open_items}`}</Button>
          </div>
        ) : null}

        {done ? (
          <EmptyState title="All verdicts decided" description={b.buckets.waiting ? `${plural(b.buckets.waiting, 'service')} waiting on GAC Pro content` : undefined}
            action={<span className="gp-actions">
              {canWrite ? saveBtn(downloadFirst || saved !== null || compareFirst ? 'ghost' : 'primary') : null}
              {downloadBtn(saved === null && !compareFirst && (downloadFirst || !canWrite) ? 'primary' : 'ghost')}
              {saved === null ? compareBtn(compareFirst ? 'primary' : 'ghost') : null}
            </span>} />
        ) : null}

        {b.items.length ? (
          <div className="gp-inv-tabs" role="group" aria-label="Show">
            <button type="button" aria-pressed={tab === 'attention'} onClick={() => switchTab('attention')}>{`Needs you · ${b.attention_count}`}</button>
            <button type="button" aria-pressed={tab === 'all'} onClick={() => switchTab('all')}>{`All · ${b.items.length}`}</button>
          </div>
        ) : null}

        {b.items.length === 0 ? (
          <EmptyState title="No services added yet" description="Add the services your product depends on; each gets a verdict here."
            action={<Button compact variant="primary" onClick={onOpenInventory}>Review the services you depend on</Button>} />
        ) : rows.length === 0 ? (
          done ? null : <EmptyState title="Nothing in this list" action={<Button compact onClick={() => switchTab('all')}>Show all verdicts</Button>} />
        ) : (
          <ul className="gp-cb-rows" aria-labelledby="vb-h" ref={list}>
            {rows.map((it) => (
              <VerdictRow key={it.item_id} slug={slug} program={program} item={it} canWrite={canWrite} ctx={ctx} primary={it.item_id === topId && saved === null} readAt={readAt}
                checking={busy === `check:${it.item_id}`} ask={ask}
                panel={open?.itemId === it.item_id ? open.panel : null}
                onOpen={(p, opener) => setOpen(p ? { itemId: it.item_id, panel: p, opener } : null)} onClose={closePanel}
                onCheck={(opener) => check(it, opener)}
                onChanged={(msg, opts) => changed(it, msg, opts)} onBoardWrite={(fn, msg) => boardWrite(it, fn, msg)} />
            ))}
          </ul>
        )}

        {canWrite && b.items.length && !done ? (
          <div className="gp-vb-foot">
            {saveBtn(!blocking && !topId && saved === null ? 'primary' : 'ghost')}
            {blocking ? <span id="vb-save-why" className="gp-dim">{`${plural(blocking, 'service')} still ${blocking === 1 ? 'needs' : 'need'} answers`} <button type="button" className="gp-cb-textbtn" onClick={() => switchTab('attention')}>Show them</button></span> : null}
            {adminOnly ? <span className="gp-vb-ask">{`${plural(adminOnly, 'service')} ${adminOnly === 1 ? 'needs' : 'need'} an admin`}{ask('record the provider’s coverage', 'GAC Pro verdicts: provider coverage needs an admin')}</span> : null}
            {last ? <span className="gp-dim">{`Last saved ${fmtDate(last.created_at)} by ${last.created_by_name ?? 'a team member'} · ${plural(last.item_count, 'verdict')}`}</span> : null}
            {compareBtn('ghost')}
          </div>
        ) : null}
        {done && last ? <p className="gp-dim">{`Last saved ${fmtDate(last.created_at)} by ${last.created_by_name ?? 'a team member'} · ${plural(last.item_count, 'verdict')}`}</p> : null}
      </div>
      {toast && !toastApi ? <Toast message={toast.message} details={toast.details} version={toast.version} returnFocus={heading.current} onDismiss={() => setToast(null)} /> : null}
    </Card>
  );
}

// ---- one row ------------------------------------------------------------------------------------------------

function VerdictRow({ slug, program, item, canWrite, ctx, primary, checking, ask, panel, onOpen, onClose, onCheck, onChanged, onBoardWrite, readAt = {} }: {
  slug: string; program: string; item: Item; canWrite: boolean; ctx: { prefix: string; program: string; uncheckable?: ReadonlySet<string> }; primary: boolean; checking: boolean;
  /** When GAC Pro last read each listing source (built-UI #8), by source key. */
  readAt?: Readonly<Record<string, string | null>>;
  ask: (verb: string, subject: string) => ReactNode;
  panel: Panel | null; onOpen: (p: Panel | null, opener: HTMLElement | null) => void; onClose: () => void; onCheck: (opener: HTMLElement | null) => Promise<void>;
  onChanged: (msg: string, opts?: { keepOpen?: boolean; confirm?: boolean; q2?: boolean }) => Promise<void>; onBoardWrite: (fn: () => Promise<{ board: VerdictBoard }>, msg: string) => Promise<void>;
}) {
  const v = item.verdict;
  const [why, setWhy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const action = rowAction(item, canWrite, ctx);
  const otherDiffers = item.other_level.verdict !== v.verdict;
  const panelId = `vb-panel-${item.item_id}`;
  const nameId = `vb-name-${item.item_id}`;
  const variant = primary ? 'primary' : 'secondary';
  const toggle = (p: Panel, opener: HTMLElement | null) => { setFormError(null); if (panel === p) onClose(); else onOpen(p, opener); };
  const attestedQ3 = item.answers.q3?.provenance === 'attested';
  const attestedQ4 = item.answers.q4?.provenance === 'attested';
  const waiting = item.attention === 'waiting';
  /** A board write from a form: its error shows inside the form. */
  const write = async (fn: () => Promise<{ board: VerdictBoard }>, msg: string) => {
    setFormError(null);
    try { await onBoardWrite(fn, msg); } catch (e) { setFormError(errMsg(e, 'That could not be saved.')); throw e; }
  };

  let act: ReactNode = null;
  if (action.kind === 'waiting') act = null;
  else if (action.kind === 'reader') act = <span className="gp-dim">Needs an admin</span>;
  else if (action.kind === 'ask_admin') act = <span className="gp-vb-askrow"><span className="gp-dim">{action.line}</span>{ask(action.verb, action.subject)}</span>;
  else if (action.kind === 'boundary') {
    act = <span className="gp-vb-askrow">
      <Link data-row-action className="gp-linkbtn gp-linkbtn--compact" data-variant={primary ? 'primary' : undefined} to={action.href} aria-label={`${action.label} · ${item.name}`}>{action.label}</Link>
      {ask(action.verb, action.subject)}
    </span>;
  } else if (action.kind === 'link') act = <Link data-row-action className="gp-linkbtn gp-linkbtn--compact" data-variant={primary ? 'primary' : undefined} to={action.href} aria-label={`${action.label} · ${item.name}`}>{action.label}</Link>;
  else if (action.kind === 'check_coverage') {
    act = <Button data-row-action compact variant={variant} aria-busy={checking || undefined} aria-disabled={checking || undefined}
      onClick={(e) => { if (!checking) void onCheck(e.currentTarget); }} aria-label={`${action.label} · ${item.name}`}>{checking ? 'Checking…' : action.label}</Button>;
  } else if (action.kind === 'panel') {
    act = <Button data-row-action compact variant={variant} aria-expanded={panel === action.panel} aria-controls={panelId} onClick={(e) => toggle(action.panel, e.currentTarget)} aria-label={`${action.label} · ${item.name}`}>{action.label}</Button>;
  }
  const text = (p: Panel, lbl: string) => (
    <button type="button" className="gp-cb-textbtn" aria-expanded={panel === p} aria-controls={panelId} onClick={(e) => toggle(p, e.currentTarget)} aria-label={`${lbl} · ${item.name}`}>{lbl}</button>
  );

  return (
    <li className="gp-cb-sg gp-vb-row" data-row={item.item_id} aria-labelledby={nameId}>
      <span className="gp-cb-main">
        <h4 className="gp-vb-name" id={nameId} tabIndex={-1}>{item.name}</h4>
        <span className="gp-cb-meta">
          {waiting ? <span className="gp-cb-chip">Waiting</span> : <span className="gp-cb-chip" data-tone={VERDICT_TONE[v.verdict]}>{VERDICT_LABEL[v.verdict]}</span>}
          <span>{action.kind === 'waiting' ? `${action.text}; ${verdictWhy(item).toLowerCase()}` : verdictWhy(item)}</span>
          {otherDiffers ? <span>{`At ${label(LEVEL_LABEL, item.other_level.target_impact_level, 'the other level')}: ${VERDICT_LABEL[item.other_level.verdict]}`}</span> : null}
          {item.partition_confirmation ? <span>{`Partition in force since ${fmtDate(item.partition_confirmation.confirmed_at)}`}</span> : null}
        </span>
        {/* V109-5-3: the listing per offering (never "certified") and, for Replace / Partition, the catalog's alternatives (PD-3).
            Built-UI #8: the FedRAMP Marketplace and the GovRAMP product list side by side, each with its read date, the current
            program's first; a solid chip (dashed means "not counted"); it wraps at narrow widths. */}
        <span className="gp-cb-meta" data-part="listing">
          {[
            { key: 'fedramp_marketplace', text: `FedRAMP Marketplace · ${listingText(item, label(LEVEL_LABEL, item.verdict.target_impact_level, 'this level'))}` },
            { key: 'govramp_product_list', text: `GovRAMP product list · ${govrampListingText(item)}` },
          ].sort((a, b) => Number(b.key === 'govramp_product_list' && program === 'govramp') - Number(a.key === 'govramp_product_list' && program === 'govramp'))
            .map((c) => <span key={c.key} className="sw-listing" data-source={c.key}>{c.text}{readAt[c.key] !== undefined ? <span className="sw-listing-when">{` · ${sourceFreshness(readAt[c.key]).text}`}</span> : null}</span>)}
          {(v.verdict === 'replace' || v.verdict === 'partition') && item.alternatives?.length
            ? <span data-part="alternatives">{`Alternatives: ${item.alternatives.map((a) => a.name).join(' · ')}`}</span> : null}
        </span>
        <details className="gp-cb-why" open={why} onToggle={(e) => setWhy((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>How we decided<span className="sr-only">{` for ${item.name}`}</span></summary>
          <dl className="gp-inv-src gp-vb-answers">
            {answerLines(item).map((l) => (
              <div key={l.key}><dt>{l.label}</dt><dd>{l.value}{l.note ? ` · ${l.note}` : ''}{l.source ? <span className="gp-dim">{` · ${l.source}`}</span> : null}</dd></div>
            ))}
            <div><dt>What it does</dt><dd>{item.function ? label(FUNCTION_LABEL, item.function, 'Set') : 'Not set'}</dd></div>
          </dl>
          {canWrite ? (
            <span className="gp-actions gp-vb-edits">
              {text('answer', 'Change the federal-data answer')}
              {text('function', 'Change what it does')}
              {item.can_override_q3 && !attestedQ3 ? text('coverage', 'The coverage is wrong') : null}
              {item.can_override_q3 && attestedQ3 ? <ConfirmAction label="Withdraw your coverage answer" name={item.name} onConfirm={() => onBoardWrite(() => withdrawCoverage(slug, program, item.item_id), 'Coverage answer withdrawn')} /> : null}
              {attestedQ4 ? <ConfirmAction label="Withdraw your evidence answer" name={item.name} onConfirm={() => onBoardWrite(() => withdrawEvidence(slug, program, item.item_id), 'Evidence answer withdrawn')} /> : null}
              {item.can_edit_tpr ? text('tpr', 'Edit the disclosure') : null}
              {item.partition_confirmation ? <ConfirmAction label="Undo the partition mark" name={item.name} verb="Undo" onConfirm={() => onBoardWrite(() => markPartition(slug, program, item.item_id, 'withdraw'), 'Partition mark withdrawn')} /> : null}
            </span>
          ) : null}
        </details>
      </span>
      <span className="gp-cb-act">{act}</span>
      {panel ? (
        <div id={panelId} className="gp-vb-panel" data-esc-owner="" onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onClose(); } }}>
          {formError ? <Alert variant="error" onDismiss={() => setFormError(null)}>{formError}</Alert> : null}
          {panel === 'answer' ? (
            <AutoFocus><ClassificationForm slug={slug} canWrite={canWrite} setError={setFormError}
              onAnswered={async (msg) => {
                const head = msg.split(' · ')[0] ?? 'Saved';
                const keep = head === 'One more question' || head === 'Suggestion ready';
                await onChanged(head === 'Saved' ? 'Answer saved' : head, { keepOpen: keep, confirm: head === 'Suggestion ready', q2: head === 'One more question' });
              }}
              item={{ item_id: item.item_id, name: item.name, review: { federal: { q1: (item.answers.q1?.value as string | undefined) ?? null, q2: (item.answers.q2?.value as string | undefined) ?? null, suggested: item.suggestions > 0 } } }} /></AutoFocus>
          ) : null}
          {panel === 'function' ? <FunctionPicker slug={slug} item={item} onCancel={onClose} onSaved={() => onChanged('Function saved')} setFormError={setFormError} /> : null}
          {panel === 'coverage' ? <CoverageForm slug={slug} program={program} item={item} onCancel={onClose} onSave={(fn, covered) => write(fn, covered ? 'Coverage recorded' : 'Marked not covered')} /> : null}
          {panel === 'evidence' ? <EvidenceForm item={item} onCancel={onClose} onSave={(method) => write(() => attestEvidence(slug, program, item.item_id, method), 'Evidence method recorded')} /> : null}
          {panel === 'tpr' && item.external_service_id ? <TprForm slug={slug} program={program} item={item} onCancel={onClose} onSaved={() => onChanged('Disclosure saved')} setFormError={setFormError} /> : null}
          {panel === 'partition' ? (
            <PartitionPanel item={item} onCancel={onClose} onConfirm={() => write(() => markPartition(slug, program, item.item_id, 'confirm'), 'Partition marked in force')} />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** One-click actions that can't be undone ask first: "<label>? Yes / Cancel" (busy guarded). */
function ConfirmAction({ label: lbl, name, verb = 'Withdraw', onConfirm }: { label: string; name: string; verb?: string; onConfirm: () => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const yes = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (asking) yes.current?.focus(); }, [asking]);
  if (!asking) return <button ref={opener} type="button" className="gp-cb-textbtn" onClick={() => setAsking(true)} aria-label={`${lbl} · ${name}`}>{lbl}</button>;
  const cancel = () => { setAsking(false); window.requestAnimationFrame(() => opener.current?.focus()); };
  return (
    <span className="gp-vb-confirm" role="group" aria-label={`${lbl} · ${name}`} data-esc-owner="" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); cancel(); } }}>
      <span>{`${verb}?`}</span>
      <button ref={yes} type="button" className="gp-cb-textbtn" aria-busy={busy || undefined} aria-disabled={busy || undefined}
        onClick={() => { if (busy) return; setBusy(true); void onConfirm().catch(() => undefined).finally(() => setBusy(false)); }}>{busy ? `${verb === 'Undo' ? 'Undoing' : 'Withdrawing'}…` : 'Yes'}</button>
      <button type="button" className="gp-cb-textbtn" onClick={cancel}>Cancel</button>
    </span>
  );
}

/** Moves focus to the first field inside on mount and scrolls it into view (H11). */
function AutoFocus({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('input[type="radio"]:checked, select, textarea, input:not([type="radio"]), button');
    el?.focus();
    el?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  return <div ref={ref}>{children}</div>;
}

/** Focus the first invalid field after a failed validation. */
function focusInvalid(form: HTMLFormElement | null) {
  window.requestAnimationFrame(() => form?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
}

function useBusy() {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => { if (busy) return; setBusy(true); try { await fn(); } catch { /* shown in the form */ } finally { setBusy(false); } };
  return { busy, run };
}

const Actions = ({ busy, onCancel }: { busy: boolean; onCancel: () => void }) => (
  <div className="gp-actions">
    <Button type="submit" compact variant="primary" aria-busy={busy || undefined} aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save'}</Button>
    <Button compact variant="ghost" onClick={onCancel}>Cancel</Button>
  </div>
);

function FunctionPicker({ slug, item, onSaved, onCancel, setFormError }: { slug: string; item: Item; onSaved: () => Promise<void>; onCancel: () => void; setFormError: (e: string | null) => void }) {
  const [fn, setFn] = useState(item.function ?? item.suggested_function ?? '');
  const [err, setErr] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);
  const { busy, run } = useBusy();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!fn) { setErr('Choose what it does.'); focusInvalid(form.current); return; }
    void run(async () => { try { await setInventoryFunction(slug, item.item_id, fn); setErr(null); await onSaved(); } catch (x) { setFormError(errMsg(x, 'The function could not be saved.')); throw x; } });
  };
  return (
    <AutoFocus>
      <form ref={form} className="gp-inline-form gp-inv-panel" onSubmit={submit} aria-label={`What ${item.name} does`}>
        <Field id={`vb-fn-${item.item_id}`} label={`What does ${item.name} do?`} error={err ?? undefined}>
          <Select value={fn} onChange={(e) => { setFn(e.currentTarget.value); setErr(null); }}>
            <option value="">Choose</option>
            {Object.entries(FUNCTION_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
        </Field>
        <Actions busy={busy} onCancel={onCancel} />
      </form>
    </AutoFocus>
  );
}

const DOC_KINDS: Array<[CoverageDocumentKind, string]> = [
  ['fedramp_authorization', 'FedRAMP authorization or CRM'], ['soc2_type2', 'SOC 2 Type II report'], ['bridge_letter', 'Bridge letter'], ['other', 'Other written confirmation'],
];
const OTHER = '__other__';
/** The provider's offerings the coverage check looked at, by display name. */
export const offeringName = (o: Item['q3_offerings'][number], provider: string) =>
  `${provider}${o.class ? ` · Class ${o.class}` : ''} (${label(LEVEL_LABEL, o.impact_level, 'unknown level')})`;

/**
 * B2 / H1 + owner decision 2026-10-09: the person's coverage answer, either way, in ONE request.
 *   It's covered      the offering + the provider's written confirmation (a new document) + why.
 *   It's not covered  why (required); the verdict then reads Replace, so a replacement can be planned.
 */
function CoverageForm({ slug, program, item, onSave, onCancel }: {
  slug: string; program: string; item: Item; onSave: (fn: () => Promise<{ board: VerdictBoard }>, covered: boolean) => Promise<void>; onCancel: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  // A conflict starts at "It's covered" (the provider's confirmation settles it, ADR-0072 Q2); otherwise the likelier
  // correction first: a service shown as covered is questioned as "not covered", and vice versa.
  const computedCovered = !item.verdict.missing.includes('q3.coverage_conflict') && (item.answers.q3?.value as { status?: string } | undefined)?.status === 'satisfiable';
  const [f, setF] = useState({ covered: computedCovered ? 'no' : 'yes', pick: item.q3_offerings.length ? '' : OTHER, other: '', kind: 'fedramp_authorization' as CoverageDocumentKind, from: today, to: '', reference: '', rationale: '', why_not: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const form = useRef<HTMLFormElement>(null);
  const { busy, run } = useBusy();
  const set = (k: keyof typeof f, v: string) => { setF((x) => ({ ...x, [k]: v })); setErr((e) => ({ ...e, [k]: '' })); };
  const covered = f.covered === 'yes';
  const offering = f.pick === OTHER ? f.other.trim() : (() => { const o = item.q3_offerings.find((x) => x.offering_key === f.pick); return o ? offeringName(o, item.provider_name) : ''; })();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const missing: Record<string, string> = {};
    if (!covered) {
      if (!f.why_not.trim()) missing.why_not = 'Say why it’s not covered.';
    } else {
      if (!f.pick) missing.pick = 'Choose the offering that covers it.';
      else if (!offering) missing.other = 'Name the offering that covers it.';
      if (!f.reference.trim()) missing.reference = 'Say where the confirmation is.';
      if (!f.to) missing.to = 'Choose when it ends.';
      if (!f.rationale.trim()) missing.rationale = 'Say why it covers this service.';
    }
    if (Object.keys(missing).length) { setErr(missing); focusInvalid(form.current); return; }
    if (!covered) { void run(() => onSave(() => overrideCoverage(slug, program, item.item_id, { covered: false, rationale: f.why_not.trim() }), false)); return; }
    void run(() => onSave(() => overrideCoverage(slug, program, item.item_id, {
      offering, rationale: f.rationale.trim(), document: { kind: f.kind, covers_from: f.from, covers_to: f.to, reference: f.reference.trim() },
    }), true));
  };
  if (!item.external_service_id) return <p className="gp-dim">Add this service to your register first.</p>;
  const id = (k: string) => `vb-cov-${item.item_id}-${k}`;
  return (
    <AutoFocus>
      <form ref={form} className="gp-inline-form gp-inv-panel" onSubmit={submit} aria-label={`Provider coverage · ${item.name}`}>
        <fieldset className="gp-vb-choice">
          <legend>{`Does the provider cover ${item.name}?`}</legend>
          <Radio id={id('yes')} name={id('covered')} value="yes" checked={covered} onChange={() => set('covered', 'yes')} label="It’s covered" description="Add the provider’s confirmation" />
          <Radio id={id('no')} name={id('covered')} value="no" checked={!covered} onChange={() => set('covered', 'no')} label="It’s not covered" description="Plan a replacement" />
        </fieldset>
        {covered ? (
          <>
            <Field id={id('pick')} label="Which offering covers it" error={err.pick || undefined}>
              <Select value={f.pick} onChange={(e) => set('pick', e.currentTarget.value)}>
                {item.q3_offerings.length ? <option value="">Choose</option> : null}
                {item.q3_offerings.map((o) => <option key={o.offering_key} value={o.offering_key}>{offeringName(o, item.provider_name)}</option>)}
                <option value={OTHER}>Other…</option>
              </Select>
            </Field>
            {f.pick === OTHER ? <Field id={id('other')} label="The offering’s name" error={err.other || undefined}><Input value={f.other} onChange={(e) => set('other', e.currentTarget.value)} /></Field> : null}
            <Field id={id('kind')} label="The provider’s confirmation">
              <Select value={f.kind} onChange={(e) => set('kind', e.currentTarget.value)}>{DOC_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
            </Field>
            <Field id={id('reference')} label="Where it is" hint="A document name or link" error={err.reference || undefined}><Input value={f.reference} onChange={(e) => set('reference', e.currentTarget.value)} /></Field>
            <Field id={id('from')} label="Covers from"><Input type="date" value={f.from} onChange={(e) => set('from', e.currentTarget.value)} /></Field>
            <Field id={id('to')} label="Covers until" error={err.to || undefined}><Input type="date" value={f.to} onChange={(e) => set('to', e.currentTarget.value)} /></Field>
            <Field id={id('rationale')} label="Why it covers this service" error={err.rationale || undefined}><Textarea rows={2} value={f.rationale} onChange={(e) => set('rationale', e.currentTarget.value)} /></Field>
          </>
        ) : (
          <Field id={id('why_not')} label="Why it’s not covered" hint="For example, the provider could not confirm it in writing" error={err.why_not || undefined}>
            <Textarea rows={2} value={f.why_not} onChange={(e) => set('why_not', e.currentTarget.value)} />
          </Field>
        )}
        <Actions busy={busy} onCancel={onCancel} />
      </form>
    </AutoFocus>
  );
}

/** B3: no collector exists for this service: record how the evidence is collected (attested). */
function EvidenceForm({ item, onSave, onCancel }: { item: Item; onSave: (method: string) => Promise<void>; onCancel: () => void }) {
  const [method, setMethod] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);
  const { busy, run } = useBusy();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!method.trim()) { setErr('Say how you collect the evidence.'); focusInvalid(form.current); return; }
    void run(() => onSave(method.trim()));
  };
  return (
    <AutoFocus>
      <form ref={form} className="gp-inline-form gp-inv-panel" onSubmit={submit} aria-label={`Evidence · ${item.name}`}>
        <Field id={`vb-ev-${item.item_id}`} label={`How do you collect evidence for ${item.name}?`} hint="For example, a script that exports its audit log every night" error={err ?? undefined}>
          <Textarea rows={2} value={method} onChange={(e) => { setMethod(e.currentTarget.value); setErr(null); }} />
        </Field>
        <Actions busy={busy} onCancel={onCancel} />
      </form>
    </AutoFocus>
  );
}

/** B4: the chosen pattern in plain words, then "Mark partitioned". */
function PartitionPanel({ item, onConfirm, onCancel }: { item: Item; onConfirm: () => Promise<void>; onCancel: () => void }) {
  const { busy, run } = useBusy();
  const kind = patternKind(item.verdict.partition_pattern_id);
  const after = item.verdict.residual?.verdict === 'out_of_scope' ? 'Once it is in force, the service is out of scope.' : 'Once it is in force, document it as a third party.';
  return (
    <AutoFocus>
      <div className="gp-inv-panel" role="group" aria-label={`Partition · ${item.name}`}>
        <p><b>{label(PATTERN_LABEL, kind, 'Keep federal data off this service')}</b></p>
        <p className="gp-dim">{after}</p>
        <div className="gp-actions">
          <Button compact variant="primary" aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={() => void run(onConfirm)}>{busy ? 'Saving…' : 'Mark partitioned'}</Button>
          <Button compact variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </AutoFocus>
  );
}

/** The four disclosure fields, prefilled; only what changed is sent. */
function TprForm({ slug, program, item, onSaved, onCancel, setFormError }: { slug: string; program: string; item: Item; onSaved: () => Promise<void>; onCancel: () => void; setFormError: (e: string | null) => void }) {
  const fields = Object.keys(TPR_LABEL) as Array<keyof Item['tpr']>;
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((f) => [f, item.tpr[f] ?? ''])));
  const [err, setErr] = useState<string | null>(null);
  const { busy, run } = useBusy();
  const changedFields = fields.filter((f) => (values[f] ?? '').trim() !== (item.tpr[f] ?? '').trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!changedFields.length) { setErr('Nothing changed yet.'); return; }
    void run(async () => {
      try {
        await updateServiceTpr(slug, program, item.external_service_id!, Object.fromEntries(changedFields.map((f) => [f, values[f]!.trim() || null])));
        await onSaved();
      } catch (x) { setFormError(errMsg(x, 'The disclosure could not be saved.')); throw x; }
    });
  };
  return (
    <AutoFocus>
      <form className="gp-inline-form gp-inv-panel" onSubmit={submit} aria-label={`Disclosure for ${item.name}`}>
        {err ? <p className="gp-dim" role="status">{err}</p> : null}
        {fields.map((f) => (
          <Field key={f} id={`vb-tpr-${item.item_id}-${f}`} label={label(TPR_LABEL, f, 'Disclosure field')} hint={!(item.tpr[f] ?? '').trim() ? 'Missing' : undefined}>
            <Textarea rows={2} value={values[f] ?? ''} onChange={(e) => { const val = e.currentTarget.value; setValues((x) => ({ ...x, [f]: val })); setErr(null); }} />
          </Field>
        ))}
        <Actions busy={busy} onCancel={onCancel} />
      </form>
    </AutoFocus>
  );
}
